import { useEffect, useState } from 'react';
import { Panel, Flex, Input, Button, CellList, CellSimple, Typography, Spinner } from '@maxhub/max-ui';
import type {
  AdminUser,
  AdminStats,
  Tariff,
  PaymentDetails,
} from '../types/models.ts';
import {
  getAdminUsers,
  approveUser,
  getAdminStats,
  getAdminTariffs,
  createAdminTariff,
  deleteAdminTariff,
  getAdminPaymentDetails,
  saveAdminPaymentDetails,
  getAdminHouses,
  createAdminHouse,
} from '../api/admin.ts';
import { formatPeriod } from '../utils/period.ts';
import type { House } from '../types/models.ts';

type Tab = 'users' | 'houses' | 'tariffs' | 'payment' | 'stats';

interface AdminPageProps {
  onBack: () => void;
}

const TAB_LABELS: Record<Tab, string> = {
  users: '👥 Польз.',
  houses: '🏠 Дома',
  tariffs: '💰 Тарифы',
  payment: '🏦 Реквизиты',
  stats: '📊 Стат.',
};

export function AdminPage({ onBack }: AdminPageProps) {
  const [tab, setTab] = useState<Tab>('users');

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      {/* Header */}
      <Flex align="center" gap={12} style={{ marginBottom: '1rem' }}>
        <Button mode="link" onClick={onBack}>← Назад</Button>
        <Typography.Headline variant="medium-strong">Администрирование</Typography.Headline>
      </Flex>

      {/* Tabs */}
      <Flex gap={4} wrap="wrap" style={{ marginBottom: '1rem' }}>
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <Button
            key={t}
            mode={tab === t ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
          </Button>
        ))}
      </Flex>

      {/* Content */}
      <div>
        {tab === 'users' && <UsersTab />}
        {tab === 'houses' && <HousesTab />}
        {tab === 'tariffs' && <TariffsTab />}
        {tab === 'payment' && <PaymentTab />}
        {tab === 'stats' && <StatsTab />}
      </div>
    </Panel>
  );
}

// ─── Users Tab ────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAdminUsers()
      .then(setUsers)
      .catch(() => setError('Не удалось загрузить пользователей'))
      .finally(() => setLoading(false));
  }, []);

  async function handleApprove(userId: string) {
    try {
      const updated = await approveUser(userId);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch {
      alert('Ошибка при подтверждении пользователя');
    }
  }

  if (loading) return <Panel centeredX style={{ padding: '2rem' }}><Spinner size={24} /></Panel>;
  if (error) return <Typography.Label style={{ display: 'block', color: '#c62828', padding: '1rem', textAlign: 'center' }}>{error}</Typography.Label>;

  const pending = users.filter((u) => !u.isApproved);
  const approved = users.filter((u) => u.isApproved);

  return (
    <div>
      {pending.length > 0 && (
        <section>
          <Typography.Headline variant="small-strong" style={{ display: 'block', margin: '1rem 0 0.5rem' }}>
            ⏳ Ожидают подтверждения ({pending.length})
          </Typography.Headline>
          <CellList mode="island" style={{ marginBottom: '1rem' }}>
            {pending.map((u) => (
              <CellSimple
                key={u.id}
                title={u.fullName ?? '—'}
                subtitle={[u.phone ?? 'нет телефона', u.email ?? 'нет email', u.accountLinks.map((l) => l.platform).join(', ')].filter(Boolean).join(' · ')}
                after={
                  <Button mode="primary" size="small" onClick={() => handleApprove(u.id)}>
                    Подтвердить
                  </Button>
                }
              />
            ))}
          </CellList>
        </section>
      )}

      <section>
        <Typography.Headline variant="small-strong" style={{ display: 'block', margin: '1rem 0 0.5rem' }}>
          ✅ Подтверждённые ({approved.length})
        </Typography.Headline>
        {approved.length === 0 ? (
          <Typography.Label style={{ display: 'block', opacity: 0.5, padding: '1rem', textAlign: 'center' }}>
            Нет пользователей
          </Typography.Label>
        ) : (
          <CellList mode="island">
            {approved.map((u) => (
              <CellSimple
                key={u.id}
                title={u.fullName ?? '—'}
                subtitle={[u.phone ?? 'нет телефона', `Роль: ${u.role}`].join(' · ')}
                after={<Typography.Label style={{ color: '#2e7d32', fontWeight: 600 }}>✓ Активен</Typography.Label>}
              />
            ))}
          </CellList>
        )}
      </section>
    </div>
  );
}

// ─── Houses Tab ───────────────────────────────────────────────────────────────

function HousesTab() {
  const [houses, setHouses] = useState<House[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [buildingNumber, setBuildingNumber] = useState('');
  const [fullAddress, setFullAddress] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAdminHouses()
      .then(setHouses)
      .catch(() => setError('Не удалось загрузить дома'))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!buildingNumber.trim()) return;
    setSaving(true);
    try {
      const house = await createAdminHouse({
        buildingNumber: buildingNumber.trim(),
        fullAddress: fullAddress.trim() || undefined,
      });
      setHouses((prev) => [house, ...prev]);
      setBuildingNumber('');
      setFullAddress('');
      setShowForm(false);
    } catch {
      alert('Ошибка при создании дома');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Panel centeredX style={{ padding: '2rem' }}><Spinner size={24} /></Panel>;
  if (error) return <Typography.Label style={{ display: 'block', color: '#c62828', padding: '1rem', textAlign: 'center' }}>{error}</Typography.Label>;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ margin: '1rem 0 0.5rem' }}>
        <Typography.Headline variant="small-strong">Дома ({houses.length})</Typography.Headline>
        <Button mode="secondary" size="small" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Отмена' : '+ Добавить'}
        </Button>
      </Flex>

      {showForm && (
        <Panel
          mode="secondary"
          style={{ borderRadius: 10, padding: '0.75rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <Input
            mode="primary"
            placeholder="Номер дома *"
            value={buildingNumber}
            onChange={(e) => setBuildingNumber(e.target.value)}
          />
          <Input
            mode="primary"
            placeholder="Полный адрес"
            value={fullAddress}
            onChange={(e) => setFullAddress(e.target.value)}
          />
          <Button mode="primary" stretched loading={saving} disabled={saving} onClick={handleCreate}>
            {saving ? 'Сохранение...' : 'Создать'}
          </Button>
        </Panel>
      )}

      {houses.length === 0 ? (
        <Typography.Label style={{ display: 'block', opacity: 0.5, padding: '1rem', textAlign: 'center' }}>Домов нет</Typography.Label>
      ) : (
        <CellList mode="island">
          {houses.map((h) => (
            <CellSimple
              key={h.id}
              title={h.fullAddress ?? `Дом ${h.buildingNumber}`}
              subtitle={`№ ${h.buildingNumber}`}
            />
          ))}
        </CellList>
      )}
    </div>
  );
}

// ─── Tariffs Tab ──────────────────────────────────────────────────────────────

function TariffsTab() {
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    periodFrom: '',
    coldWaterRate: '',
    hotWaterRate: '',
    wastewaterRate: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAdminTariffs()
      .then(setTariffs)
      .catch(() => setError('Не удалось загрузить тарифы'))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!form.periodFrom || !form.coldWaterRate || !form.hotWaterRate || !form.wastewaterRate) {
      alert('Заполните все обязательные поля');
      return;
    }
    setSaving(true);
    try {
      const tariff = await createAdminTariff({
        periodFrom: form.periodFrom,
        coldWaterRate: parseFloat(form.coldWaterRate),
        hotWaterRate: parseFloat(form.hotWaterRate),
        wastewaterRate: parseFloat(form.wastewaterRate),
        notes: form.notes || undefined,
      });
      setTariffs((prev) => [tariff, ...prev]);
      setForm({ periodFrom: '', coldWaterRate: '', hotWaterRate: '', wastewaterRate: '', notes: '' });
      setShowForm(false);
    } catch {
      alert('Ошибка при создании тарифа');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(tariffId: string) {
    if (!confirm('Удалить тариф?')) return;
    try {
      await deleteAdminTariff(tariffId);
      setTariffs((prev) => prev.filter((t) => t.id !== tariffId));
    } catch {
      alert('Ошибка при удалении тарифа');
    }
  }

  if (loading) return <Panel centeredX style={{ padding: '2rem' }}><Spinner size={24} /></Panel>;
  if (error) return <Typography.Label style={{ display: 'block', color: '#c62828', padding: '1rem', textAlign: 'center' }}>{error}</Typography.Label>;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ margin: '1rem 0 0.5rem' }}>
        <Typography.Headline variant="small-strong">Тарифы ({tariffs.length})</Typography.Headline>
        <Button mode="secondary" size="small" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Отмена' : '+ Добавить'}
        </Button>
      </Flex>

      {showForm && (
        <Panel
          mode="secondary"
          style={{ borderRadius: 10, padding: '0.75rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <Typography.Label style={{ display: 'block' }}>Действует с *</Typography.Label>
          <Input mode="primary" type="date" value={form.periodFrom} onChange={(e) => setForm((f) => ({ ...f, periodFrom: e.target.value }))} />
          <Typography.Label style={{ display: 'block' }}>ХВ (руб/м³) *</Typography.Label>
          <Input mode="primary" type="number" step="0.01" placeholder="Холодная вода" value={form.coldWaterRate} onChange={(e) => setForm((f) => ({ ...f, coldWaterRate: e.target.value }))} />
          <Typography.Label style={{ display: 'block' }}>ГВ (руб/м³) *</Typography.Label>
          <Input mode="primary" type="number" step="0.01" placeholder="Горячая вода" value={form.hotWaterRate} onChange={(e) => setForm((f) => ({ ...f, hotWaterRate: e.target.value }))} />
          <Typography.Label style={{ display: 'block' }}>ВО (руб/м³) *</Typography.Label>
          <Input mode="primary" type="number" step="0.01" placeholder="Водоотведение" value={form.wastewaterRate} onChange={(e) => setForm((f) => ({ ...f, wastewaterRate: e.target.value }))} />
          <Typography.Label style={{ display: 'block' }}>Примечание</Typography.Label>
          <Input mode="primary" placeholder="Примечание" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <Button mode="primary" stretched loading={saving} disabled={saving} onClick={handleCreate}>
            {saving ? 'Сохранение...' : 'Создать тариф'}
          </Button>
        </Panel>
      )}

      {tariffs.length === 0 ? (
        <Typography.Label style={{ display: 'block', opacity: 0.5, padding: '1rem', textAlign: 'center' }}>Тарифов нет</Typography.Label>
      ) : (
        <CellList mode="island">
          {tariffs.map((t) => (
            <CellSimple
              key={t.id}
              title={`С ${t.periodFrom}${t.periodTo ? ` по ${t.periodTo}` : ' (текущий)'}`}
              subtitle={[`ХВ: ${t.coldWaterRate}`, `ГВ: ${t.hotWaterRate}`, `ВО: ${t.wastewaterRate} руб/м³`, t.notes].filter(Boolean).join(' · ')}
              after={
                <Button mode="secondary" size="small" appearance="negative" onClick={() => handleDelete(t.id)}>
                  Удалить
                </Button>
              }
            />
          ))}
        </CellList>
      )}
    </div>
  );
}

// ─── Payment Details Tab ──────────────────────────────────────────────────────

function PaymentTab() {
  const [details, setDetails] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrString, setQrString] = useState<string | null>(null);
  const [form, setForm] = useState({
    recipientName: '',
    inn: '',
    kpp: '',
    checkingAccount: '',
    bankName: '',
    bic: '',
    correspondentAccount: '',
    sbpPhone: '',
    paymentPurposePrefix: 'Оплата воды за',
  });

  useEffect(() => {
    getAdminPaymentDetails()
      .then((d) => {
        setDetails(d);
        setForm({
          recipientName: d.recipientName,
          inn: d.inn,
          kpp: d.kpp ?? '',
          checkingAccount: d.checkingAccount,
          bankName: d.bankName,
          bic: d.bic,
          correspondentAccount: d.correspondentAccount,
          sbpPhone: d.sbpPhone ?? '',
          paymentPurposePrefix: d.paymentPurposePrefix ?? 'Оплата воды за',
        });
      })
      .catch(() => {/* нет реквизитов — форма пустая */})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const saved = await saveAdminPaymentDetails({
        recipientName: form.recipientName,
        inn: form.inn,
        kpp: form.kpp || undefined,
        checkingAccount: form.checkingAccount,
        bankName: form.bankName,
        bic: form.bic,
        correspondentAccount: form.correspondentAccount,
        sbpPhone: form.sbpPhone || undefined,
        paymentPurposePrefix: form.paymentPurposePrefix || undefined,
      });
      setDetails(saved);
      alert('Реквизиты сохранены');
    } catch {
      alert('Ошибка при сохранении реквизитов');
    } finally {
      setSaving(false);
    }
  }

  async function handleTestQr() {
    try {
      const res = await import('../api/admin.ts').then((m) => m.getAdminQrTest(100));
      setQrString(res.qrString);
    } catch {
      alert('Нет реквизитов для генерации QR');
    }
  }

  if (loading) return <Panel centeredX style={{ padding: '2rem' }}><Spinner size={24} /></Panel>;

  const fields: Array<{ key: keyof typeof form; label: string; maxLength?: number }> = [
    { key: 'recipientName', label: 'Получатель *' },
    { key: 'inn', label: 'ИНН *', maxLength: 12 },
    { key: 'kpp', label: 'КПП', maxLength: 9 },
    { key: 'checkingAccount', label: 'Расчётный счёт *', maxLength: 20 },
    { key: 'bankName', label: 'Банк *' },
    { key: 'bic', label: 'БИК *', maxLength: 9 },
    { key: 'correspondentAccount', label: 'Корр. счёт *', maxLength: 20 },
    { key: 'sbpPhone', label: 'Телефон СБП', maxLength: 20 },
    { key: 'paymentPurposePrefix', label: 'Назначение платежа' },
  ];

  return (
    <div>
      <Typography.Headline variant="small-strong" style={{ display: 'block', margin: '1rem 0 0.5rem' }}>
        Реквизиты ТСН
      </Typography.Headline>
      {details && (
        <Typography.Label style={{ display: 'block', opacity: 0.5, marginBottom: '0.75rem' }}>
          Обновлено: {new Date(details.updatedAt).toLocaleDateString('ru-RU')}
        </Typography.Label>
      )}

      <Panel
        mode="secondary"
        style={{ borderRadius: 10, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
      >
        {fields.map(({ key, label, maxLength }) => (
          <div key={key}>
            <Typography.Label style={{ display: 'block', marginBottom: '0.25rem' }}>{label}</Typography.Label>
            <Input
              mode="primary"
              value={form[key]}
              maxLength={maxLength}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
          </div>
        ))}

        <Button mode="primary" stretched loading={saving} disabled={saving} onClick={handleSave}>
          {saving ? 'Сохранение...' : 'Сохранить реквизиты'}
        </Button>

        <Button mode="secondary" stretched onClick={handleTestQr}>
          Тест QR (100 руб.)
        </Button>

        {qrString && (
          <Panel
            mode="secondary"
            style={{ borderRadius: 8, padding: '0.75rem', marginTop: '0.5rem' }}
          >
            <Typography.Label style={{ display: 'block', marginBottom: '0.25rem' }}>ST00012 строка:</Typography.Label>
            <code style={{ fontSize: '0.65rem', wordBreak: 'break-all', display: 'block' }}>{qrString}</code>
          </Panel>
        )}
      </Panel>
    </div>
  );
}

// ─── Stats Tab ────────────────────────────────────────────────────────────────

function StatsTab() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAdminStats()
      .then(setStats)
      .catch(() => setError('Не удалось загрузить статистику'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Panel centeredX style={{ padding: '2rem' }}><Spinner size={24} /></Panel>;
  if (error) return <Typography.Label style={{ display: 'block', color: '#c62828', padding: '1rem', textAlign: 'center' }}>{error}</Typography.Label>;
  if (!stats) return null;

  return (
    <div>
      <Typography.Headline variant="small-strong" style={{ display: 'block', margin: '1rem 0 0.5rem' }}>
        Период: {formatPeriod(stats.period)}
      </Typography.Headline>

      {/* Пользователи */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
        <StatCard label="Всего" value={stats.users.total} />
        <StatCard label="Подтверждено" value={stats.users.approved} color="#2e7d32" />
        <StatCard label="Ожидают" value={stats.users.pending} color="#e65100" />
      </div>

      {/* Дома */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
        <StatCard label="Домов" value={stats.houses.total} />
        <StatCard label="Сдали" value={stats.houses.submitted} color="#2e7d32" />
        <StatCard label="Не сдали" value={stats.houses.notSubmitted} color="#c62828" />
      </div>

      {stats.notSubmittedHouses.length > 0 && (
        <section>
          <Typography.Headline variant="small-strong" style={{ display: 'block', margin: '1rem 0 0.5rem' }}>
            ❌ Не сдали показания ({stats.notSubmittedHouses.length})
          </Typography.Headline>
          <CellList mode="island" style={{ marginBottom: '1rem' }}>
            {stats.notSubmittedHouses.map((h) => (
              <CellSimple
                key={h.id}
                title={h.fullAddress ?? `Дом ${h.buildingNumber}`}
                subtitle={h.owner ? `${h.owner.fullName ?? '—'} · ${h.owner.phone ?? 'нет телефона'}` : undefined}
              />
            ))}
          </CellList>
        </section>
      )}

      {stats.submittedHouses.length > 0 && (
        <section>
          <Typography.Headline variant="small-strong" style={{ display: 'block', margin: '1rem 0 0.5rem' }}>
            ✅ Сдали показания ({stats.submittedHouses.length})
          </Typography.Headline>
          <CellList mode="island">
            {stats.submittedHouses.map((h) => (
              <CellSimple
                key={h.id}
                title={h.fullAddress ?? `Дом ${h.buildingNumber}`}
                subtitle={h.owner?.fullName ?? '—'}
                style={{ opacity: 0.7 }}
              />
            ))}
          </CellList>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <CellList mode="island">
      <div style={{ padding: '0.75rem', textAlign: 'center' }}>
        <Typography.Headline
          variant="large-strong"
          style={{ display: 'block', color: color ?? undefined }}
        >
          {value}
        </Typography.Headline>
        <Typography.Label style={{ display: 'block', opacity: 0.6, marginTop: '0.25rem', fontSize: '0.7rem' }}>
          {label}
        </Typography.Label>
      </div>
    </CellList>
  );
}
